/** @jsxImportSource @emotion/react */
import React from "react";
import { css, useTheme } from "@emotion/react";
import { useLanguage } from "LanguageContext";
// @ts-ignore
import "yet-another-react-lightbox/styles.css";
import MediaPreview from "../../../CommonUi/MediaPreview/MediaPreview"
import { useAnalysis } from "../FormComponents/AnalysisResultContext";

interface CampaignCardProps {
  title: string;
  referenceFrame: string;
  advertiser: string;
  project: string;
  language: string;
  dateCreated: string;
  note: string;
  imageUrl: string;
  descriptionUrl: string;
  description: string;
  original_url: string;
  imgType: string;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  title,
  referenceFrame,
  advertiser,
  project,
  language,
  dateCreated,
  note,
  imageUrl,
  descriptionUrl,
  description,
  original_url,
  imgType,
}) => {
  const upperCaseLanguage = language ? language.toUpperCase() : "";
  const theme = useTheme();
  const languageCntxtObj = useLanguage();
  const t = languageCntxtObj.t;
  const locale = language === "fr" ? "fr-FR" : "en-US";

  const formattedDate = dateCreated ? new Date(dateCreated).toLocaleDateString(languageCntxtObj.language, {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).replace(/,/g, "") : '--';

  const cardContainer = css`
    display: flex;
    justify-content: start;
    align-items: center;
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.medium};
    padding: ${theme.spacing["5xl"]};
    border-radius: ${theme.radius.sm};
    width: 100%;
    box-sizing: border-box;
    margin-top: ${theme.spacing["5xl"]};
    flex-direction: row;

    @media (max-width: 991px) {
      padding: 24px;
      flex-direction: column;
      gap: 10px;
      align-items: center;
    }

    @media (max-width: 768px) {
      padding: 16px 20px;
      flex-direction: column;
      gap: 10px;
      align-items: center;
      margin-top: 20px;
    }
  `;

  const cardWrapper = css`
    display: flex;
    width: 70%;
    flex-direction: column;

    @media (max-width: 1180px) {
      width: 100%;
    }
  `;

  const leftStyle = css`
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    margin: 6px 0;
  `;

  const titlesContainer = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    align-items: end;
    text-align: right;
    width: 25%;

    @media (max-width: 1168px) {
      width: 50%;
    }
  `;

  const descriptionContainer = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    max-width: 70%;
    align-items: start;
    width: 75%;
  `;

  const heading3_medium = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: 36px;
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    @media (max-width: 920px) {
      font-size: 30px;
    }
  `;

  const bodyMedium = css`
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: 16px;
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    @media (max-width: 668px) {
      font-size: 12px;
    }
  `;

  const bodySemiBold = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: 16px;
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    @media (max-width: 668px) {
      font-size: 12px;
    }
  `;

  return (
    <div css={cardContainer}>
      <div css={cardWrapper}>
        <div css={leftStyle} style={{ alignItems: "baseline" }}>
          <div css={titlesContainer} style={{ justifyContent: "center" }}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_campaign")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={heading3_medium}>{title}</div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_project")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>{project}</div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_advertiser")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>{advertiser}</div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_referenceFrame")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>{`${referenceFrame}`}</div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_analysisLanguage")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>
              {
              upperCaseLanguage === 'EN'
                ? t('label_english')
                : t('label_french')
              }
            </div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>
              {t("analyse_result_card_title_dateCreated")}
            </div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>{formattedDate}</div>
          </div>
        </div>
        <div css={leftStyle}>
          <div css={titlesContainer}>
            <div css={bodySemiBold}>{t("analyse_result_card_title_note")}</div>
          </div>
          <div css={descriptionContainer}>
            <div css={bodyMedium}>{note}</div>
          </div>
        </div>
      </div>

      <MediaPreview  imageUrl={imageUrl}
      originalUrl={original_url}
      imgType={imgType}
      description={description}
      title={title}
      t={t}
      />

    </div>
  );
};

export default CampaignCard;